/*
 * Copyright (c) Nexign, JSC, 2019-2020.
 */
/**
 *
 * @author Fedorov Maxim
 * @author Evgenij Tyan
 * @author Yakov Gushchin
 * @author Anton Panasenko
 * @author Aleksandr Perkhunov
 * @version 1.3.0
 * @since 1.0.1
 */

import { FC, Fragment } from 'react';

import moment from 'moment';
import { connect } from 'react-redux';
import { Dispatch } from 'redux';
import { createStructuredSelector } from 'reselect';

import { LinkBlock } from '../../../../../../components/common';
import GTMOperations from '../../../../../../modules/analytics/GoogleTagManager/GTMOperations';
import { IGTMPushAnalyticPayload } from '../../../../../../modules/analytics/GoogleTagManager/types/GTMActionTypes';
import { ICommonTranslation, IPageDashboardTranslation } from '../../../../../../modules/internationalization/types';
import { makeSelectLocalStorageSelectedAccount } from '../../../../../../modules/storage/storageSelectors';
import { IRootReducer } from '../../../../../../reducers/rootReducer';
import { CLIENT_ID, RoutePath } from '../../../../../../routes/constants/routesConstants';
import { widgetCostsConfiguration } from '../../WidgetCosts/constant/widgetCostsConstants';
import { IWidgetCostsRedirectToPaymentsPayload } from '../../WidgetCosts/types/actionsType';
import { widgetCostsRedirectToPayments } from '../../WidgetCosts/widgetCostsActions';
import { IWidgetSearchAccountsItem } from '../../WidgetSearchAccounts/types/widgetSearchAccountsTypes';
import { WidgetSubscriberMaxCostsListSuccessStyled } from '../styled/widgetSubscriberMaxCostsStyled';
import { ISubscribersMaxCost } from '../widgetSubscriberMaxCostsType';
import WidgetSubscriberMaxCostsList from './WidgetSubscriberMaxCostsList';

interface IDispatchProps {
    widgetCostsRedirectToPayments: (payload: IWidgetCostsRedirectToPaymentsPayload) => void;
}

interface IWidgetSubscriberMaxCostsSuccessfulProps {
    pushGtmAnalytic: (payload: IGTMPushAnalyticPayload) => void;
    subscribers: ISubscribersMaxCost[];
    translation: IPageDashboardTranslation & ICommonTranslation;
}

export type TWidgetSubscriberMaxCostsSuccessful =
    IConnectedProps & IWidgetSubscriberMaxCostsSuccessfulProps & IDispatchProps;

export const WidgetSubscriberMaxCostsSuccessful: FC<TWidgetSubscriberMaxCostsSuccessful> = props => {
    const { subscribers, translation } = props;

    const onLinkClick = (): void => {
        const date = moment();
        const dateFrom = date.startOf('month').format(widgetCostsConfiguration.dateFormatRedirect);
        const dateTo = date.endOf('month').format(widgetCostsConfiguration.dateFormatRedirect);
        props.widgetCostsRedirectToPayments({
            dateFrom, dateTo,
        });

        props.pushGtmAnalytic({
            operation: GTMOperations.widgetSubscriberMaxCosts.onClickSubscriberLinkWidgetSubscriberMaxCosts(
                translation.widgets.subscriberMaxCosts.successful.btnText,
            ),
            withAccountType: true,
            withSusrId: true,
            withUserRole: true,
        })
    };

    const getUrlToExpenses = () => {
        const accountId = props.selectedAccount.id.toString();
        return RoutePath.expensesAnalyzerAccountIdSubscribers.replace(CLIENT_ID, accountId);
    }

    return (
        <Fragment>

            <WidgetSubscriberMaxCostsListSuccessStyled>
                <WidgetSubscriberMaxCostsList
                    pushGtmAnalytic={props.pushGtmAnalytic}
                    subscribers={subscribers}
                    translation={translation}
                />
            </WidgetSubscriberMaxCostsListSuccessStyled>

            <div>
                <LinkBlock
                    dataName='redirectToSubscribersCosts'
                    url={getUrlToExpenses()}
                    onClick={onLinkClick}
                >
                    {translation.widgets.subscriberMaxCosts.successful.btnText}
                </LinkBlock>
            </div>

        </Fragment>
    );
};

export interface IConnectedProps {
    selectedAccount: IWidgetSearchAccountsItem;
}

const mapStateToProps = createStructuredSelector<IRootReducer, IConnectedProps>({
    selectedAccount: makeSelectLocalStorageSelectedAccount(),
});

export const mapDispatchToProps = (dispatch: Dispatch): IDispatchProps => ({
    widgetCostsRedirectToPayments: (payload: IWidgetCostsRedirectToPaymentsPayload) =>
        dispatch(widgetCostsRedirectToPayments(payload)),
});

export default connect(mapStateToProps, mapDispatchToProps)(WidgetSubscriberMaxCostsSuccessful);
